// @ts-check
import React, { Component, StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import cx from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect, Dispatch } from 'react-redux'; // eslint-disable-line no-unused-vars
import {
  referralDashboardNextPageSlotsRequested,
  referralDashboardPreviousPageSlotsRequested,
} from 'shared/actions/ReferralProfiles/ReferralDashboardActions';
import { showModal } from 'shared/actions/UnboundModalActions';
import Spinner from 'shared/components/Spinner';
import Icon from 'shared/components/Icon';
import { ProviderMappingResult, AppointmentSlot } from '../types'; // eslint-disable-line no-unused-vars
import classes from './AvailableSlotsPicker.module.scss';

const SHORT_SLOT_LIST_LENGTH = 4;

/** @typedef {{
  slot: AppointmentSlot,
  onSlotClick: (slot: AppointmentSlot) => void
}} SlotLinkPropsType */

/** @type { React.ValidationMap<SlotLinkPropsType> } */
const slotLinkPropTypes = {
  slot: PropTypes.object.isRequired,
  onSlotClick: PropTypes.func.isRequired,
};

/** @type { StatelessComponent<SlotLinkPropsType> } */
const SlotLink = ({ slot, onSlotClick }) => (
  <a data-slot-id={slot.id} onClick={() => onSlotClick(slot)} className={classes.appointment_slot}>
    {moment(slot.startTime, 'YYYY-MM-DDTHH:mm:ss').format('h:mm A')}
  </a>
);

SlotLink.displayName = 'SlotLink';
SlotLink.propTypes = slotLinkPropTypes;

/** @type { StatelessComponent<{}> } */
const LoadingSlots = () => (
  <div className={classes.LoadingSlots}>
    <div className={classes.LoadingSlots_inner}>
      <Spinner />
      <span className={classes.LoadingSlots__label}>Loading...</span>
    </div>
  </div>
);

LoadingSlots.displayName = 'LoadingSlots';

/** @typedef {{
  matchingResult: ProviderMappingResult,
  shortenSlotList?: boolean,
  onSlotClick: (slot: AppointmentSlot) => void,
  dispatch: Dispatch<any>
}} AvailableSlotsPickerPropsType */

/** @type { React.ValidationMap<AvailableSlotsPickerPropsType> } */
const propTypes = {
  matchingResult: PropTypes.object.isRequired,
  shortenSlotList: PropTypes.bool.isRequired,
  onSlotClick: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

/** @type { StatelessComponent<AvailableSlotsPickerPropsType> } */
const AvailableSlotsPicker = ({ matchingResult, shortenSlotList, onSlotClick, dispatch }) => {
  const {
    provider: { id: providerId },
    isFetchingSlots,
    groupedAppointmentSlots,
    referralProfileId,
  } = matchingResult;

  return (
    <div className={classes.AvailableSlotsPicker}>
      <div className={classes.date_headers}>
        <ul>
          {groupedAppointmentSlots
            .map(g => moment(g.date, 'YYYY-M-D'))
            .map(m => (
              <li key={m.format('M/D/YY')}>
                <span className={classes.day_name}>{m.format('ddd')}</span> {m.format('M/D/YY')}
              </li>
            ))}
        </ul>
      </div>
      <div className={classes.day_columns_section}>
        {isFetchingSlots ? <LoadingSlots /> : null}

        <table className={classes.slots}>
          <tbody>
            <tr>
              {groupedAppointmentSlots.map((g, jdx) => (
                <td key={jdx} className={classes.day_column}>
                  {g.slots.map((slot, idx) => {
                    if (shortenSlotList) {
                      if (idx > SHORT_SLOT_LIST_LENGTH) {
                        return null;
                      }
                      if (idx === SHORT_SLOT_LIST_LENGTH) {
                        return (
                          <a
                            key={slot.id}
                            className={classes.show_more}
                            onClick={() => dispatch(showModal({ name: 'provider', providerId }))}
                          >
                            Show more...
                          </a>
                        );
                      }
                    }
                    return <SlotLink key={slot.id} slot={slot} onSlotClick={onSlotClick} />;
                  })}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div
        data-spec-id="prev-week"
        className={cx(classes.week_nav, classes.prev)}
        onClick={() =>
          dispatch(
            referralDashboardPreviousPageSlotsRequested(referralProfileId, matchingResult.id)
          )
        }
      >
        <Icon name="chevron-left" />
      </div>
      <div
        data-spec-id="next-week"
        className={cx(classes.week_nav, classes.next)}
        onClick={() =>
          dispatch(referralDashboardNextPageSlotsRequested(referralProfileId, matchingResult.id))
        }
      >
        <Icon name="chevron-right" />
      </div>
    </div>
  );
};

AvailableSlotsPicker.displayName = 'AvailableSlotsPicker';
AvailableSlotsPicker.propTypes = propTypes;

AvailableSlotsPicker.defaultProps = {
  shortenSlotList: false,
};

export default connect()(AvailableSlotsPicker);
